import { useEffect, useState } from "react";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type EmailLog } from "../lib/api";
import { whenLast } from "../lib/dates";

const pad = (n: number) => String(n).padStart(2, "0");
const STATUS: Record<EmailLog["messages"][number]["status"], string> = {
  logged: "Logged",
  unmatched: "Not matched",
  rejected: "Refused",
  discarded: "Discarded",
};

/**
 * Each person's private address for logging emails: BCC it, or forward an email to it, and the
 * email lands on the timeline of every candidate and client contact in it.
 */
export function EmailLogging() {
  const [log, setLog] = useState<EmailLog | null>(null);
  const [senders, setSenders] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirmNew, setConfirmNew] = useState(false);
  const load = useAction();
  const act = useAction();

  const show = (l: EmailLog) => {
    setLog(l);
    setSenders(l.senders.join("\n"));
  };
  useEffect(() => void load.run(async () => show(await api.emailLog())), []); // eslint-disable-line react-hooks/exhaustive-deps

  const newAddress = () =>
    void act.run(async () => {
      show(await api.newEmailLogAddress());
      setConfirmNew(false);
      setCopied(false);
    });

  const sendersChanged = log && senders.trim() !== log.senders.join("\n");

  return (
    <>
      <div className="page-head">
        <p className="label">Email logging</p>
        <h1 className="display">
          BCC it, and it's <em>logged</em>.
        </h1>
        <p className="lede">
          Your own private address. Put it in BCC when you email a candidate or client, or forward an email to it, and KuduRMS adds the
          email to the timeline of everyone in it that it knows: candidates by their email, client contacts on their company's log.
        </p>
      </div>
      <ErrorNotice error={load.error ?? act.error} />

      {log && !log.enabled && (
        <p className="notice">
          Not switched on yet: KuduRMS needs its own email domain first. You can make your address now; it starts working as soon as
          the domain is set up.
        </p>
      )}

      {log && (
        <section className="section" aria-labelledby="addr-h">
          <div className="section-head">
            <h2 id="addr-h" className="label">
              Your logging address
            </h2>
          </div>
          {log.address ? (
            <>
              <div className="copy-row">
                <code>{log.address}</code>
                <button type="button" className="btn secondary" onClick={() => void navigator.clipboard.writeText(log.address!).then(() => setCopied(true))}>
                  {copied ? "Copied" : "Copy address"}
                </button>
              </div>
              <p className="hint">
                Tip: save it in your email contacts as "KuduRMS log" so it's quick to add to BCC. Keep it to yourself: anything sent from
                your addresses to it gets logged.
              </p>
              {confirmNew ? (
                <p className="button-row">
                  <span className="label alert">The current address will stop working.</span>
                  <button type="button" className="link-btn danger" disabled={act.pending} onClick={newAddress}>
                    Make a new address
                  </button>
                  <button type="button" className="link-btn" onClick={() => setConfirmNew(false)}>
                    Keep this one
                  </button>
                </p>
              ) : (
                <button type="button" className="link-btn" onClick={() => setConfirmNew(true)}>
                  Make a new address
                </button>
              )}
            </>
          ) : (
            <button type="button" className="btn" disabled={act.pending} onClick={newAddress}>
              Create my address
            </button>
          )}
        </section>
      )}

      {log?.address && (
        <section className="section" aria-labelledby="senders-h">
          <div className="section-head">
            <h2 id="senders-h" className="label">
              Addresses you send from
            </h2>
          </div>
          <p className="hint">
            Only emails sent from your own addresses are logged, so nobody else can add notes by writing to your logging address. Your
            login, <strong>{log.loginEmail}</strong>, always counts. Add any others you send from, one per line.
          </p>
          <label className="field">
            <span className="label">Other addresses</span>
            <textarea rows={3} value={senders} onChange={(e) => setSenders(e.target.value)} placeholder="e.g. you@your-agency.co.uk" />
          </label>
          {sendersChanged && (
            <div className="button-row spaced-top">
              <button
                type="button"
                className="btn"
                disabled={act.pending}
                onClick={() =>
                  void act.run(async () =>
                    show(
                      await api.setEmailLogSenders(
                        senders
                          .split(/[\s,;]+/)
                          .map((s) => s.trim())
                          .filter(Boolean),
                      ),
                    ),
                  )
                }
              >
                Save addresses
              </button>
            </div>
          )}
        </section>
      )}

      {log?.address && (
        <section className="section" aria-labelledby="recent-h">
          <div className="section-head">
            <h2 id="recent-h" className="label">
              Recent emails
            </h2>
            <span className="label">kept 30 days</span>
          </div>
          {log.messages.length === 0 ? (
            <p className="empty-line">Nothing yet. BCC your address on your next email to a candidate.</p>
          ) : (
            <ol className="index">
              {log.messages.map((m, i) => (
                <li key={m.id} className={m.status === "discarded" ? "inactive" : undefined}>
                  <span className="num" aria-hidden>
                    {pad(i + 1)}
                  </span>
                  <span className="main">
                    <span className="primary">
                      <span className={`answer ${m.status === "logged" ? "interview" : m.status === "rejected" ? "" : "no"}`}>{STATUS[m.status]}</span>{" "}
                      {m.subject}
                    </span>
                    <span className="secondary">
                      {whenLast(m.receivedAt)}
                      {m.from && <> · from {m.from}</>}
                      {m.reason && <> · {m.reason}</>}
                    </span>
                  </span>
                  <span className="actions">
                    {(m.status === "unmatched" || m.status === "rejected") && (
                      <button
                        type="button"
                        className="link-btn"
                        disabled={act.pending}
                        onClick={() =>
                          void act.run(async () => {
                            await api.discardLoggedEmail(m.id);
                            show(await api.emailLog());
                          })
                        }
                      >
                        Discard
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}
    </>
  );
}
